import {
  ChangeEvent,
  // ChangeEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Box,
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  StackProps,
  styled,
  Typography
} from '@mui/material';
import { StyledParams } from 'types/common';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { useLocation, useNavigate } from 'react-router-dom';
import OutlinedInput from '@mui/material/OutlinedInput';
import { StorageServices } from 'services/storage';
import { UserInfo } from 'services/user';
import { getUserInfo } from 'services/auth';
import { useUser } from 'contexts/user';

type LayoutAdminTableProps = {
  children: ReactNode;
  textButton?: string;
  onClickButton?: () => void;
  element?: ReactElement;
  title?: string;
  search?: string;
  onSearch?: (value: string) => void;
  wrapperProps?: StackProps;
};
const storage = new StorageServices();

export const LayoutAdminTable = ({
  children,
  element,
  textButton,
  onClickButton,
  title,
  search,
  wrapperProps,
  onSearch
}: LayoutAdminTableProps) => {
  const navigate = useNavigate();
  const accessToken = storage.getAccessToken();
  const { setUser, user } = useUser();

  const handleLogout = useCallback(() => {
    storage.removeAccessToken();
    storage.removeRefreshToken();
    storage.removeRole();
    setUser(null);
    navigate('/admin/login');
  }, [storage]);
  const { pathname } = useLocation();
  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const refScroll = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (refScroll.current) {
      refScroll.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pathname]);

  useEffect(() => {
    if (!accessToken) {
      setUser(null);
      return;
    }
    if (user?.id) return;
    (async () => {
      try {
        const { user }: { user: UserInfo } = await getUserInfo();
        setUser(user);
      } catch (error) {
        console.log(error);
      }
    })();
  }, [accessToken, setUser, user?.id]);

  return (
    <>
      <WrapperHeader>
        <Stack>
          {onSearch && (
            <InputStyled
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onSearch(e.target.value as string)}
              startAdornment={
                <InputAdornment position="end" sx={{ paddingRight: '8px' }}>
                  <img alt={'user'} src={'/icons/search.svg'} />
                </InputAdornment>
              }
              placeholder={'Search'}
            />
          )}
        </Stack>
        <WrapperRightHeader>
          <WrapperAvatar>
            <AccountCircleIcon sx={{ width: '44px', height: ' 44px' }} />
          </WrapperAvatar>
          <WrapperName>
            {user?.role === 'ROOT_ADMIN' ? (
              <TextStyled>Master Admin</TextStyled>
            ) : user?.firstName || user?.lastName ? (
              <TextStyled>
                {user?.firstName} {user?.lastName}
              </TextStyled>
            ) : (
              <TextStyled>Admin</TextStyled>
            )}
            <TextRoleStyled>{storage.getRole()?.replace('_', ' ')}</TextRoleStyled>
          </WrapperName>
          <WrapperIcon onClick={() => setOpenMenu(true)}>
            <img alt={'icon'} src={'/icons/chevron_down.svg'} />
          </WrapperIcon>
          <MenuStyled
            open={openMenu}
            onClose={() => setOpenMenu(false)}
            MenuListProps={{
              'aria-labelledby': 'basic-button'
            }}>
            <MenuItem style={{ gap: '10px' }} onClick={handleLogout}>
              <LogoutIcon />
              <Typography style={{ fontWeight: 500 }}>Logout</Typography>
            </MenuItem>
          </MenuStyled>
        </WrapperRightHeader>
      </WrapperHeader>
      <ScrollStyled ref={refScroll}>
        <WrapperLayout>
          <Wrapper
            style={{ width: 'calc(100vw - 330px)', overflowX: 'auto', maxWidth: '2000px' }}
            {...wrapperProps}>
            <HeaderStyled>
              {title && <TitleStyled>{title}</TitleStyled>}
              {(textButton || element) && (
                <Stack style={{ minHeight: '56px' }}>
                  {textButton && (
                    <Stack style={{ height: '56px', minWidth: '100px' }}>
                      <ButtonCustom
                        style={{ padding: '12px 24px' }}
                        onClick={onClickButton}
                        label={textButton}
                      />
                    </Stack>
                  )}
                  {element && element}
                </Stack>
              )}
            </HeaderStyled>
            <Stack>{children}</Stack>
          </Wrapper>
        </WrapperLayout>
      </ScrollStyled>
    </>
  );
};
const WrapperLayout = styled(Stack)(() => ({
  background: '#FFF',
  maxWidth: '100%',
  padding: '40px 0px 40px 0px',
  alignItems: 'flex-start',
  marginLeft: '40px'
}));
const Wrapper = styled(Stack)(() => ({
  background: '#FFF'
}));
const HeaderStyled = styled(Stack)(() => ({
  flexDirection: 'row',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: '14px'
}));
const TitleStyled = styled(Typography)(({ theme }: StyledParams) => ({
  color: theme.colors.dark1,
  fontSize: 20,
  fontWeight: 700
}));

const MenuStyled = styled(Menu)(() => ({
  '.MuiPaper-root': {
    minWidth: 130,
    top: '53px !important',
    right: '32px !important',
    bottom: 'unset !important',
    left: 'unset !important'
  }
}));
const TextRoleStyled = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: '600',
  color: '#565656'
}));
const WrapperHeader = styled(Stack)(() => ({
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '70px',
  background: '#F4F5F6',
  borderBottom: '1px solid #BABAB6',
  flexDirection: 'row',
  padding: '0 30px 0 40px'
}));
const ScrollStyled = styled(Stack)(() => ({
  flexGrow: 1,
  overflowY: 'auto',
  background: '#fff',
  width: '100%',
  height: 'calc(100dvh - 70px)'
}));
const InputStyled = styled(OutlinedInput)(() => ({
  minHeight: '38px',
  width: '388px',
  height: '38px',
  backgroundColor: '#fff',
  borderRadius: 20,
  '& .MuiInputBase-input': {
    backgroundColor: '#fff',
    padding: '8px 8px',
    borderRadius: 20
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderBottom: `1px solid #ccc`,
    boxShadow: 'inset 0 5px 10px rgba(0, 0, 0, 0.05)',
    borderRadius: 20
  }
}));
const WrapperRightHeader = styled(Stack)(() => ({
  gap: 20,
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row'
}));
const WrapperAvatar = styled(Box)(() => ({
  height: 44,
  width: 44,
  borderRadius: 22
}));
const WrapperIcon = styled(Button)(() => ({
  height: 18,
  width: 18,
  borderRadius: 9,
  border: '0.2px solid #5C5C5C',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 0
}));
const WrapperName = styled(Stack)(() => ({
  gap: '4px'
}));
const TextStyled = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: '700',
  color: '#202224'
}));
